export interface Reading {
  /** Account readings only: which course it sits in. */
  courseId?: string | null;
  /** Account readings only: whether the original PDF is still kept. */
  hasPdf?: boolean;
  /** The read the student chose at upload. Absent on older readings. */
  mode?: RebuildMode;
  /** Account list only: which reads are already saved for this reading. */
  rebuilds?: RebuildMode[];
  id: string;
  title: string;
  sourceText: string;
  createdAt: number;
  pdfBlob?: Blob;
  pages?: PageText[];
}

export interface PageText {
  /** 1-based, in the PDF's page order. */
  pageNumber: number;
  text: string;
}

export type CutKind =
  "hedging" | "repetition" | "signposting" | "aside" | "furniture";
export type DiagramKind = "process" | "comparison" | "taxonomy" | "two-axis";

export interface Source {
  /** Absent for pasted readings, which have no pages. */
  page?: number;
  /** Verbatim from the source. */
  quote: string;
}

export interface Span {
  text: string;
  loadBearing: boolean;
  source?: Source;
}

export interface ProseBlock {
  kind: "prose";
  spans: Span[];
}

export interface RefutationBlock {
  kind: "refutation";
  belief: string;
  tempting: string;
  correction: string;
}

export interface DiagramBlock {
  kind: "diagram";
  diagramKind: DiagramKind;
  mermaid: string;
}

export type Block = ProseBlock | RefutationBlock | DiagramBlock;

export interface Segment {
  id: string;
  /** A claim, not a topic label. */
  heading: string;
  blocks: Block[];
  sourcePages: number[];
}

export interface Term {
  term: string;
  definition: string;
  page?: number;
}

export interface Cut {
  kind: CutKind;
  page?: number;
  text: string;
  /** Set when repeated instances are grouped into one entry. */
  count?: number;
}

export type RebuildMode = "quick" | "deep";

export interface Rebuild {
  readingId: string;
  mode: RebuildMode;
  title: string;
  terms: Term[];
  segments: Segment[];
  cuts: Cut[];
  fidelityRisks: string[];
  originalWords: number;
  rebuiltWords: number;
  originalMinutes: number;
  rebuiltMinutes: number;
  generatedAt: number;
}

export type Confidence = 1 | 2 | 3 | 4; // Guessing, Unsure, Fairly sure, Certain

export interface McqOption {
  text: string;
  correct: boolean;
  /** The real misunderstanding this option is built from. Empty for the correct one. */
  misunderstanding: string;
  /** Shown after the reveal, only if the student picked this option. */
  whyTempting: string;
}

export interface Mcq {
  id: string;
  question: string;
  options: McqOption[]; // exactly 4, exactly one correct
  segmentId?: string; // absent for calibration questions
  /**
   * Why the correct option is correct, shown at the reveal whichever option
   * the student picked. Optional on the type, but required of anything newly
   * generated: sessions persisted before this field existed must still load.
   */
  whyCorrect?: string;
}

export interface Answer {
  questionId: string;
  pickedIndex: number;
  confidence: Confidence;
  answeredAt: number;
}

export interface FreeResponse {
  promptId: string;
  segmentId: string;
  text: string;
  reply?: string; // the model's response, never a grade
}

export interface TeachBackTurn {
  student: string;
  reply: string;
}

export interface ReviewSlot {
  dueAt: number; // epoch ms, local midnight
  questionIds: string[]; // checkpoints only, never a re-read
  completedAt?: number;
}

export interface DeepReadSession {
  id: string;
  readingId: string;
  examDate?: number; // epoch ms, optional
  calibrationQuestions: Mcq[]; // answers never revealed
  checkpoints: Mcq[];
  // Persisted alongside calibrationQuestions/checkpoints, from the same
  // questions call, so a resumed session's readingSteps are built from the
  // exact same prompts rather than losing them on reload.
  selfExplanationPrompts: { segmentId: string; prompt: string }[];
  sketchPrompts: { segmentId: string; prompt: string }[];
  answers: Answer[];
  freeResponses: FreeResponse[];
  sketches: { segmentId: string; note: string; image?: Blob }[];
  teachBack: TeachBackTurn[];
  unlockedSegmentIndex: number; // the gate
  completedAt?: number;
  schedule: ReviewSlot[];
}

export interface ReadingStore {
  addReading(reading: Reading): Promise<void>;
  listReadings(): Promise<Reading[]>;
  getReading(id: string): Promise<Reading | undefined>;
  saveRebuild(rebuild: Rebuild): Promise<void>;
  getRebuild(
    readingId: string,
    mode?: RebuildMode,
  ): Promise<Rebuild | undefined>;
  saveDeepReadSession(session: DeepReadSession): Promise<void>;
  getDeepReadSession(id: string): Promise<DeepReadSession | undefined>;
  listDeepReadSessionsForReading(readingId: string): Promise<DeepReadSession[]>;
  renameReading(id: string, title: string): Promise<void>;
  /** Removes the reading and everything made from it. */
  deleteReading(id: string): Promise<void>;
}

/** The same PDF is already in this account; `readingId` is the one to open. */
export class DuplicateReadingError extends Error {
  constructor(
    message: string,
    readonly readingId: string,
    readonly title: string,
  ) {
    super(message);
    this.name = "DuplicateReadingError";
  }
}
